/**
 * Action Chips Component
 * Displays detected actions as inline chips above the chat input
 */

import React, { useEffect } from 'react';
import { View, Text, Pressable, ScrollView, Platform } from 'react-native';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { ActionTemplate, MatchedAction } from '@utils/semanticActionMapper';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { formatActionDescriptionForEasyMode } from '@utils/easyModeActionFormatter';

interface ActionChipsProps {
  actions: MatchedAction[];
  onExecute: (template: ActionTemplate, data?: any) => void;
  onDismiss: (intent: string) => void;
  onDismissAll: () => void;
  onFeedback?: (actionIntent: string, feedbackType: 'upvote' | 'downvote') => void;
  disableAbsolutePositioning?: boolean; // Allow parent to control positioning
}

const ActionChips: React.FC<ActionChipsProps> = ({
  actions,
  onExecute,
  onDismiss,
  onDismissAll,
  onFeedback,
  disableAbsolutePositioning = false
}) => {
  const theme = themeSelector();
  const [votedActions, setVotedActions] = React.useState<Set<string>>(new Set());

  // Auto-dismiss after 30 seconds
  useEffect(() => {
    if (actions.length > 0) {
      const timer = setTimeout(() => {
        onDismissAll();
      }, 30000);

      return () => clearTimeout(timer);
    }
  }, [actions.length, onDismissAll]);

  // Handle feedback submission
  const handleFeedback = (actionIntent: string, feedbackType: 'upvote' | 'downvote') => {
    if (votedActions.has(actionIntent)) {
      return; // Prevent duplicate voting
    }

    setVotedActions(prev => new Set([...prev, actionIntent]));
    onFeedback?.(actionIntent, feedbackType);
  };

  if (actions.length === 0) {
    return null;
  }

  // Get category color
  const getCategoryColor = (category: string, destructive: boolean) => {
    if (destructive) return '#ef4444'; // red
    switch (category) {
      case 'navigation':
        return '#3b82f6'; // blue
      case 'data':
        return '#10b981'; // green
      case 'ui':
        return '#8b5cf6'; // purple
      case 'modal':
        return '#f59e0b'; // orange
      default:
        return '#6b7280'; // gray
    }
  };

  return (
    <Animatable.View
      animation="slideInDown"
      duration={300}
      style={{
        // Only apply absolute positioning if not disabled
        ...(disableAbsolutePositioning ? {} : {
          position: 'absolute',
          bottom: 140,
          left: 0,
          right: 0,
          zIndex: 10000,
          marginHorizontal: 10,
        }),
        paddingHorizontal: 15,
        paddingVertical: 10,
        backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
        borderRadius: 12,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 5,
        ...(Platform.OS === 'web' && {
          backdropFilter: 'blur(10px)',
        })
      }}
    >
      {/* Header */}
      <View style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <MaterialIcons name="auto-awesome" size={16} color={theme ? '#3b82f6' : '#60a5fa'} />
          <Text style={{
            fontSize: 12,
            fontWeight: '600',
            color: theme ? '#374151' : '#e5e7eb'
          }}>
            Detected Actions
          </Text>
        </View>

        <Pressable onPress={onDismissAll} style={{ padding: 4 }}>
          <AntDesign name="close" size={14} color={theme ? '#6b7280' : '#9ca3af'} />
        </Pressable>
      </View>

      {/* Action Chips */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          flexDirection: 'row',
          gap: 8,
          paddingRight: 10
        }}
      >
        {actions.map((matchedAction, index) => {
          const { intent, confidence, template } = matchedAction;
          const categoryColor = getCategoryColor(template.category, template.destructive);

          return (
            <Animatable.View
              key={intent}
              animation="fadeIn"
              delay={index * 100}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: theme ? 'white' : '#1e293b',
                borderRadius: 20,
                paddingVertical: 8,
                paddingLeft: 12,
                paddingRight: 6,
                borderWidth: 1.5,
                borderColor: categoryColor,
                shadowColor: categoryColor,
                shadowOffset: { width: 0, height: 1 },
                shadowOpacity: 0.3,
                shadowRadius: 3,
                elevation: 2,
                maxWidth: 250
              }}
            >
              {/* Icon indicator for data-required actions */}
              {template.requiresData && (
                <MaterialIcons
                  name="edit-note"
                  size={16}
                  color={categoryColor}
                  style={{ marginRight: 6 }}
                />
              )}

              {/* Action description */}
              <View style={{ flex: 1, marginRight: 8 }}>
                <Text
                  numberOfLines={1}
                  style={{
                    fontSize: 13,
                    fontWeight: '600',
                    color: theme ? '#1f2937' : '#f3f4f6'
                  }}
                >
                  {formatActionDescriptionForEasyMode(template.description || template.intent, template.intent)}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <Text
                    style={{
                      fontSize: 10,
                      color: theme ? '#6b7280' : '#9ca3af',
                      marginTop: 2
                    }}
                  >
                    {(confidence * 100).toFixed(0)}% match
                  </Text>
                  {matchedAction.sourcePage && (
                    <Text
                      style={{
                        fontSize: 9,
                        color: theme ? '#9ca3af' : '#6b7280',
                        marginTop: 2,
                        fontStyle: 'italic'
                      }}
                    >
                      • {matchedAction.sourcePage}
                    </Text>
                  )}
                  {matchedAction.feedbackMultiplier > 1.2 && (
                    <Text style={{ fontSize: 8, color: '#22c55e', fontWeight: '600' }}>
                      ⭐ Popular
                    </Text>
                  )}
                  {matchedAction.feedbackMultiplier < 0.8 && (
                    <Text style={{ fontSize: 8, color: '#ef4444', fontWeight: '600' }}>
                      ⚠️ Low rating
                    </Text>
                  )}
                </View>
              </View>

              {/* Execute button */}
              <Pressable
                onPress={() => onExecute(template)}
                style={({ pressed }) => ({
                  backgroundColor: categoryColor,
                  borderRadius: 14,
                  width: 28,
                  height: 28,
                  justifyContent: 'center',
                  alignItems: 'center',
                  opacity: pressed ? 0.7 : 1
                })}
              >
                <MaterialIcons name="play-arrow" size={18} color="white" />
              </Pressable>

              {/* Feedback buttons */}
              {onFeedback && !votedActions.has(intent) && (
                <View style={{ flexDirection: 'row', gap: 2, marginLeft: 4 }}>
                  <Pressable
                    onPress={() => handleFeedback(intent, 'upvote')}
                    style={({ pressed }) => ({
                      padding: 2,
                      borderRadius: 8,
                      backgroundColor: pressed ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                      opacity: pressed ? 0.7 : 1
                    })}
                  >
                    <AntDesign name="like" size={12} color="#22c55e" />
                  </Pressable>
                  <Pressable
                    onPress={() => handleFeedback(intent, 'downvote')}
                    style={({ pressed }) => ({
                      padding: 2,
                      borderRadius: 8,
                      backgroundColor: pressed ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                      opacity: pressed ? 0.7 : 1
                    })}
                  >
                    <AntDesign name="dislike" size={12} color="#ef4444" />
                  </Pressable>
                </View>
              )}

              {/* Voted state indicator */}
              {votedActions.has(intent) && (
                <View style={{ marginLeft: 4, padding: 2 }}>
                  <AntDesign name="checkcircleo" size={12} color="#22c55e" />
                </View>
              )}

              {/* Dismiss button */}
              <Pressable
                onPress={() => onDismiss(intent)}
                style={({ pressed }) => ({
                  marginLeft: 4,
                  padding: 4,
                  opacity: pressed ? 0.5 : 0.7
                })}
              >
                <AntDesign name="close" size={12} color={theme ? '#9ca3af' : '#6b7280'} />
              </Pressable>
            </Animatable.View>
          );
        })}
      </ScrollView>
    </Animatable.View>
  );
};

export default ActionChips;

